import { StateCreator } from 'zustand';
import { getEngine, getGroupedQuoteWithTypes, QuoteType } from '@lio/rule-engine';
import { GroupedAttribute, GroupedQuote } from '@lio/rule-engine/utils/quote-details';
import { shallow } from 'zustand/shallow';
import { Panel, PanelInstance, QuoteItemTypeById, StructuredQuote } from '@store/Quote/Quote.types';
import { TQuoteStore } from '@store/types/GlobalStore.type';
import { StructuredQuoteSlice } from '@store/types/StructuredQuoteSlice.type';
import { BillingAddressAutofillType } from '@pages/BindQuote/BillingCard';
import { getBillingAddressAutoFillValues, getInitialBillingValues } from '@pages/BindQuote/helpers';
import { merge } from 'lodash';
import { fieldValues } from '@pages/BindQuote/constants';
import { CodeName } from '@models/quoteItem';
import { getStructuredQuoteWithUnsavedValues } from '@store/Quote/quoteUnsavedValueOverlay';

const initialState = {
  userRoles: [],
  offering: undefined,
  _cachedOffering: undefined,
  _cachedQuoteForms: undefined,
  _cachedIsInternalUser: undefined,
  panelInstances: [],
  panelInstancesByParentInstanceId: {},
  quote: undefined,
  quoteId: undefined,
  quoteItemSearch: undefined,
  quoteFieldCanBeModified: {},
  structuredQuote: undefined,
  quoteItemTypeCodeNameToQuoteType: {},
  billingCardFields: fieldValues,
  initialBillingValues: fieldValues,
  businessAddress: null,
  unsavedFieldsKeys: [],
  isEndorsement: undefined,
};

export const createStructuredQuoteSlice: StateCreator<TQuoteStore, [], [], StructuredQuoteSlice> = (set, get) => ({
  ...initialState,
  setQuoteFieldCanBeModified: (field, canBeModified: boolean) =>
    set((state) => ({
      ...state,
      quoteFieldCanBeModified: {
        ...state.quoteFieldCanBeModified,
        [field]: canBeModified,
      },
    })),
  updateQuoteDetails: (quoteId, quote, offering, quoteItemSearch, quoteForms, isInternalUser) => {
    let revalidate = false;
    set((state) => {
      if (!quote || !offering || !quoteItemSearch) {
        return state;
      }
      if (
        quoteId !== state.quoteId ||
        !shallow(quote, state.quote) ||
        !shallow(offering, state._cachedOffering) ||
        !shallow(quoteItemSearch, state.quoteItemSearch) ||
        !shallow(quoteForms, state._cachedQuoteForms) ||
        isInternalUser !== state._cachedIsInternalUser
      ) {
        const mappedOffering = {
          ...offering,
          quoteItemTypes: offering.quoteItemTypes.map((qit) => ({
            ...qit,
            attributeTypes: qit.quoteItemAttributeTypes,
          })),
        };

        const quoteItemTypeById: QuoteItemTypeById = Object.fromEntries(
          mappedOffering.quoteItemTypes.map((qit) => [qit.id, qit]),
        );
        const quoteItemAttributeTypesById = Object.fromEntries(
          mappedOffering.quoteItemTypes.flatMap((qit) => qit.quoteItemAttributeTypes.map((qiat) => [qiat.id, qiat])),
        );

        const quoteItems = quoteItemSearch.items.map((qi) => ({
          ...qi,
          parentId: qi.parentId as string | null,
          quoteItemType: quoteItemTypeById[qi.quoteItemTypeId],
          quoteItemAttributes: qi.quoteItemAttributes.map((qia) => {
            return {
              ...qia,
              quoteItemAttributeType: quoteItemAttributeTypesById[qia.quoteItemAttributeTypeId],
            };
          }),
        }));

        const groupedQuote = getGroupedQuoteWithTypes(
          // @ts-ignore
          { ...quote, quoteItems },
          // @ts-ignore  TODO: Make offering.writingCompany not optional in API types
          mappedOffering,
          { includeInactive: true },
        ) as unknown as StructuredQuote;

        const quoteItemById = Object.fromEntries(groupedQuote.quoteItems.map((qi) => [qi.id, qi]));

        const panelsByQuoteItemTypeId =
          mappedOffering?.panels.reduce<{ [key: string]: Panel[] }>((agg, p) => {
            const { quoteItemTypeId } = p;

            agg[quoteItemTypeId] = agg[quoteItemTypeId] ?? [];
            agg[quoteItemTypeId].push(p);
            return agg;
          }, {}) ?? {};

        const panelsById = Object.fromEntries(mappedOffering?.panels.map((p) => [p.id, p]) ?? []);
        const quotePanelFieldsById: any = {};
        const panelInstances = groupedQuote.quoteItems
          .flatMap((quoteItem) => {
            const panels = panelsByQuoteItemTypeId[quoteItem.quoteItemType.id] ?? [];

            return panels.map((panel) => {
              let parentPanelQuoteItem;

              const panelSectionIds = panel.panelSections.map((s) => s.id);
              const fields = Object.values(quoteItem.attributeTypes).filter((at) =>
                panelSectionIds.includes(at.panelSectionId as string),
              );
              quotePanelFieldsById[`${panel.id}-${quoteItem.id}`] = fields;
              if (panel.parentPanelId) {
                const parentPanel = panelsById[panel.parentPanelId];
                parentPanelQuoteItem = findQuoteItemParentWithTypeId(
                  quoteItemById,
                  quoteItem.id,
                  parentPanel.quoteItemTypeId,
                );
              }
              return {
                panelInstanceId: `${panel.id}-${quoteItem.id}`,
                quoteItem,
                panel,
                parentPanelInstanceId: panel.parentPanelId
                  ? `${panel.parentPanelId}-${parentPanelQuoteItem?.id ?? 'unknown'}`
                  : undefined,
              };
            });
          })
          .sort((a, b) => a.panel.order - b.panel.order);

        const panelInstancesByParentInstanceIdUnsorted =
          panelInstances
            ?.filter((p) => p.parentPanelInstanceId)
            .reduce<{
              [key: string]: PanelInstance[];
            }>((agg, p) => {
              const parentId = p.parentPanelInstanceId as string;

              agg[parentId] = agg[parentId] ?? [];
              agg[parentId].push(p);
              return agg;
            }, {}) ?? {};

        const panelInstancesByParentInstanceId: { [key: string]: PanelInstance[] } = {};

        // sort panelInstancesByParentInstanceId first by p.order then by p.instanceNumber
        Object.entries(panelInstancesByParentInstanceIdUnsorted).forEach(([key, panelsForParentId]) => {
          let sortedItems = panelsForParentId.sort((a: any, b: any) => {
            const orderDiff = a.panel.order - b.panel.order;

            if (
              orderDiff !== 0 ||
              !a.quoteItem ||
              !b.quoteItem ||
              a.quoteItem.instanceNumber === undefined ||
              b.quoteItem.instanceNumber === undefined
            ) {
              return orderDiff;
            }

            return a.quoteItem.instanceNumber - b.quoteItem.instanceNumber;
          });

          panelInstancesByParentInstanceId[key] = sortedItems;
        });

        const quoteItemTypeCodeNameToQuoteType = Object.fromEntries(
          offering.quoteItemTypes.map((qit) => [
            qit.id,
            Object.fromEntries(qit.quoteItemAttributeTypes.map((qat) => [qat?.codeName, qat])),
          ]),
        );
        const policyQuoteItem = groupedQuote.quoteItems.find(
          (quoteItem) => quoteItem.quoteItemType.codeName === CodeName.Policy,
        );
        // is it possible to tell which panelInstances have changed and only update those?
        // do this and log the panelInstanceId of the ones that have changed
        // --- Change detection & structural sharing for panelInstances ---
        const prevPanelInstances = state.panelInstances || [];
        const prevById: Record<string, PanelInstance & { panelInstanceId: string }> = Object.create(null);
        for (const pi of prevPanelInstances as any[]) {
          prevById[pi.panelInstanceId] = pi;
        }

        const hasChanged = (prev: any, next: any): boolean => {
          if (!prev) return true;
          // Cheap comparisons first.
          if (prev.parentPanelInstanceId !== next.parentPanelInstanceId) return true;
          if (prev.panel?.order !== next.panel?.order) return true;
          if (prev.panel?.panelSections?.length !== next.panel?.panelSections?.length) return true;
          if (prev.quoteItem?.id !== next.quoteItem?.id) return true; // new quoteItem entirely

          if (
            (Object.values(prev.quoteItem?.attributeTypes ?? {}).length ?? 0) !==
            (Object.values(next.quoteItem?.attributeTypes ?? {}).length ?? 0)
          )
            // need to look at quoteItemAttributes => for each
            return true;
          // If attribute values exist, do a very shallow value diff on attribute
          const prevAttrs: GroupedAttribute[] = Object.values(prev.quoteItem?.attributeTypes ?? {});
          const nextAttrs: GroupedAttribute[] = Object.values(next.quoteItem?.attributeTypes ?? {});
          if (prevAttrs && nextAttrs) {
            for (let i = 0; i < prevAttrs.length; i++) {
              if (prevAttrs[i]?.attribute?.value !== nextAttrs[i]?.attribute?.value) return true;
              if (prevAttrs[i]?.attribute?.valueOverride !== nextAttrs[i]?.attribute?.valueOverride) return true;
            }
          }

          return false;
        };

        const changedPanelInstanceIds: string[] = [];
        const sharedPanelInstances = panelInstances.map((pi) => {
          const prev = prevById[pi.panelInstanceId];
          if (hasChanged(prev, pi)) {
            changedPanelInstanceIds.push(pi.panelInstanceId);

            return pi;
          }
          return prev;
        });

        revalidate = true;

        // Build quoteFormsV2 for the rule engine context
        const quoteFormsV2 = (quoteForms?.items ?? []).map((qf) => ({
          id: qf.id,
          formVersionId: qf.formVersion.id,
          instanceNumber: qf.instanceNumber,
          isOnQuote: qf.isOnQuote,
          formVersion: qf.formVersion,
          quoteFormAttributes: qf.quoteFormAttributes.map((qfa) => ({
            id: qfa.id,
            formAttributeTypeId: qfa.formAttributeTypeId,
            listItemGroupIndex: qfa.listItemGroupIndex,
            value: qfa.value,
            valueOverride: qfa.valueOverride,
            // formVersion.formAttributeTypes has offering overrides merged in; the raw qfa one does not.
            formAttributeType:
              qf.formVersion.formAttributeTypes.find((fat) => fat.id === qfa.formAttributeTypeId) ??
              qfa.formAttributeType,
          })),
        }));

        // Reuse the existing rule engine if one exists — just update its context.
        // Creating a new engine changes the reference, which triggers validation
        // effects in all QuotePanelInstance components simultaneously.
        // Exception: when isInternalUser changes we must create a new engine so
        // that ruleEngine subscribers (QuotePanelInstance) re-render and pick up
        // the updated context — calling updateContext alone is insufficient because
        // the reference stays stable and Zustand won't re-notify subscribers.
        const existingEngine = state.ruleEngine;
        const contextData = {
          ...groupedQuote,
          // The API returns the override only as the related object, but rules (and the backend engine,
          // which reads the Prisma row) reference the flat `~/issuingWritingCompanyId` field.
          issuingWritingCompanyId: quote.issuingWritingCompany?.id ?? null,
          quoteFormsV2,
          internalUser: isInternalUser ?? false,
        } as unknown as GroupedQuote;
        const ruleEngineContextData = getStructuredQuoteWithUnsavedValues({
          structuredQuote: contextData,
          storedAttributes: get().attributesById,
        }) as unknown as GroupedQuote;
        const internalUserUnchanged = isInternalUser === state._cachedIsInternalUser;
        let engine;
        if (existingEngine && quoteId === state.quoteId && internalUserUnchanged) {
          existingEngine.updateContext(ruleEngineContextData);
          engine = existingEngine;
        } else {
          engine = getEngine(ruleEngineContextData) as any;
        }

        return {
          quotePanelFieldsById,
          quoteId,
          quote,
          offering: mappedOffering,
          ruleEngine: engine,
          _cachedOffering: offering,
          _cachedQuoteForms: quoteForms,
          _cachedIsInternalUser: isInternalUser,
          quoteItemSearch,
          structuredQuote: contextData as any,
          isEndorsement: quote.quoteType === QuoteType.Endorsement,
          panelInstances: sharedPanelInstances,
          panelInstancesByParentInstanceId,
          quoteItemTypeCodeNameToQuoteType,
          billingCardFields: getInitialBillingValues({ billingCardFields: fieldValues, policyQuoteItem }),
          initialBillingValues: getInitialBillingValues({ billingCardFields: fieldValues, policyQuoteItem }),
        };
      }
      return state;
    });
    // leaving commented out for use of debugging later
    // const tAfterSet = performance.now();
    // console.log(
    //   `[updateQuoteDetails] structured quote recompute: ${(tAfterSet - t0).toFixed(0)}ms, revalidate: ${!!revalidate}`,
    // );
    if (revalidate) {
      get().triggerValidation();
    }
    // console.log(`[updateQuoteDetails] total (including validation): ${(performance.now() - t0).toFixed(0)}ms`);
  },
  setAutoCompleteAddress: (addressType: BillingAddressAutofillType) => {
    const structuredQuote = get().structuredQuote;
    const namedInsuredItem = structuredQuote?.quoteItems.find(
      (quoteItem) => quoteItem.quoteItemType.codeName === CodeName.NamedInsured,
    );
    const updatedFields = getBillingAddressAutoFillValues({
      businessAddress: get().businessAddress,
      addressType,
      namedInsuredItem: namedInsuredItem,
    });
    const newValues = merge({}, get().billingCardFields, updatedFields);
    set((prev) => ({
      ...prev,
      billingCardFields: newValues,
      unsavedFieldsKeys: Object.entries(newValues)
        .filter(([k, v]) => get().initialBillingValues[k]?.value !== v.value)
        ?.map(([, v]: any) => v.codeName),
    }));
  },
  setCustomerBusinessAddress: (customer) => {
    // TODO: this should probably be derivable and not stored in global state
    const businessAddress = customer.businessAddress;
    set((prev) => ({
      ...prev,
      businessAddress,
    }));
  },
  setBillingCardFields: (billingCardFields) => {
    set((prev) => ({
      ...prev,
      billingCardFields,
      unsavedFieldsKeys: Object.entries(billingCardFields)
        .filter(([k, v]) => get().initialBillingValues[k]?.value !== v.value)
        ?.map(([, v]) => v.codeName),
    }));
  },
  patchNamedInsuredCustomerName: (newName) => {
    const {
      quoteId,
      quote,
      offering,
      quoteItemSearch,
      structuredQuote,
      quoteItemTypeCodeNameToQuoteType,
      _cachedQuoteForms,
      _cachedIsInternalUser,
    } = get();
    if (!quoteId || !quote || !offering || !quoteItemSearch || !structuredQuote) return;

    const namedInsuredItem = structuredQuote.quoteItems.find(
      (qi) => qi.quoteItemType.codeName === CodeName.NamedInsured,
    );
    if (!namedInsuredItem) return;

    const customerNameAttrType = quoteItemTypeCodeNameToQuoteType[namedInsuredItem.quoteItemType.id]?.['CustomerName'];
    if (!customerNameAttrType) return;

    const patchedQuoteItemSearch = {
      ...quoteItemSearch,
      items: quoteItemSearch.items.map((qi) => {
        if (qi.id !== namedInsuredItem.id) return qi;
        return {
          ...qi,
          quoteItemAttributes: qi.quoteItemAttributes.map((qia) =>
            qia.quoteItemAttributeTypeId === customerNameAttrType.id ? { ...qia, value: newName } : qia,
          ),
        };
      }),
    };

    get().updateQuoteDetails(
      quoteId,
      quote,
      offering,
      patchedQuoteItemSearch,
      _cachedQuoteForms,
      _cachedIsInternalUser,
    );
  },
});

function findQuoteItemParentWithTypeId(
  quoteItemById: {
    [k: string]: GroupedQuote['quoteItems'][number];
  },
  quoteItemId: string,
  typeId: string,
): GroupedQuote['quoteItems'][number] | undefined {
  const quoteItem = quoteItemById[quoteItemId];
  if (quoteItem.quoteItemType.id === typeId) {
    return quoteItem;
  }
  if (quoteItem.parentId) {
    return findQuoteItemParentWithTypeId(quoteItemById, quoteItem.parentId, typeId);
  }
  return undefined;
}
